<script setup lang="ts">
import { useAttrs, onBeforeMount, watch, onUnmounted } from "vue";

import type { EventTouch } from "@/Common/Interfaces";

import { hasValue } from "@/Utils/Objects";
import { isApp } from "@/Utils/Ionic";

import { MIN_SCALE, MAX_SCALE } from "../constants";
import { log } from "@/Utils/Debug";

export interface TouchVideoCallProps {
  isZoomEnabled: boolean;
}

const props = defineProps<TouchVideoCallProps>();

const attrs: Record<string, unknown> = useAttrs();
const scale = defineModel<number>({ required: true });

const emit = defineEmits<{
  (event: "change", value: number): void;
}>();

const updateScaleVideo = (nextScale: number) => {
  scale.value = nextScale;

  emit("change", nextScale);
};

const handleZoomIn = (event: EventTouch) => {
  log("VideoCall: Zoom IN");

  if (scale.value! > MIN_SCALE) {
    const movement = Math.floor(event.distance) / 500;
    let nextScale = scale.value!;

    if (scale.value! - movement < MIN_SCALE) {
      nextScale = MIN_SCALE;
    } else {
      nextScale -= movement;
    }

    updateScaleVideo(nextScale);
  }
};

const handleZoomOut = (event: EventTouch) => {
  log("VideoCall: Zoom IN");

  if (scale.value! < MAX_SCALE) {
    const movement = Math.floor(event.distance) / 500;
    let nextScale = scale.value!;

    if (scale.value! + movement > MAX_SCALE) {
      nextScale = MAX_SCALE;
    } else {
      nextScale += movement;
    }

    updateScaleVideo(nextScale);
  }
};

const scaleWatcher = watch(scale, (newValue, oldValue) => {
  if (newValue !== oldValue && hasValue(newValue) && newValue !== scale.value) {
    log("VideoCall: Update zoom", newValue);

    scale.value = newValue;
  }
});

onBeforeMount(() => {
  if (!hasValue(scale.value)) {
    updateScaleVideo(MIN_SCALE);
  }
});

onUnmounted(() => {
  scaleWatcher();
});
</script>

<template>
  <v-touch
    v-if="isApp()"
    v-bind:class="attrs.class"
    v-bind:enabled="props.isZoomEnabled"
    v-on:pinchin="handleZoomIn"
    v-on:pinchout="handleZoomOut"
  >
    <slot />
  </v-touch>

  <div v-else v-bind:class="attrs.class">
    <slot />
  </div>
</template>

<style scoped lang="scss"></style>
